import { cn } from '@/platform/lib/utils';
import { UserIcon } from '@heroicons/react/16/solid';
import {
    ClipboardDocumentCheckIcon,
    CurrencyEuroIcon,
    PencilSquareIcon,
    PresentationChartBarIcon,
} from '@heroicons/react/24/outline';
import { Link, usePage } from '@inertiajs/react';
import qs from 'qs';

const sum = counts =>
    counts
        ? Object.values(counts).reduce((total, count) => total + count, 0)
        : undefined;

const Steps = () => {
    const { component } = usePage();
    const { auth, contract_checks, business_rules, savings, filters } =
        usePage().props;

    const baseUrl = `/customers/${auth?.user?.selected_customer_id}/services/ww-check`;

    const steps = [
        {
            name: 'Dashboard',
            href: baseUrl,
            component: 'WWCheck/DashboardView',
            icon: PresentationChartBarIcon,
        },
        {
            name: 'Business Rules',
            href: `${baseUrl}/business-rules`,
            component: 'WWCheck/BusinessRulesView',
            icon: PencilSquareIcon,
            count: sum(business_rules),
        },
        {
            name: 'Contract check',
            href: `${baseUrl}/contract-check`,
            component: 'WWCheck/ContractChecksView',
            icon: ClipboardDocumentCheckIcon,
            count: contract_checks,
        },
        {
            name: 'Besparingen',
            href: `${baseUrl}/savings`,
            component: 'WWCheck/SavingsView',
            icon: CurrencyEuroIcon,
            count: sum(savings),
        },
    ];

    return (
        <nav aria-label='WW-check stappen' className='mb-4'>
            <ol className='grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-md border border-gray-200 bg-white sm:grid-cols-4 sm:divide-y-0'>
                {steps.map((step, index) => {
                    const active = step.component === component;

                    return (
                        <li key={step.name} className='relative'>
                            <Link
                                href={`${step.href}?${qs.stringify({
                                    entity_id: filters?.entity_id || undefined,
                                    year: filters?.year || undefined,
                                    months: filters.months,
                                })}`}
                                aria-current={active ? 'page' : undefined}
                                className={cn(
                                    'group focus-visible:outline-primary-600 flex h-full items-center justify-center gap-3 px-6 py-4 focus-visible:outline-2 focus-visible:-outline-offset-2'
                                )}
                            >
                                <step.icon
                                    aria-hidden='true'
                                    className={cn(
                                        'size-8 shrink-0 transition-colors',
                                        active
                                            ? 'text-primary-600'
                                            : 'group-hover:text-primary-600 text-gray-400'
                                    )}
                                />
                                <span className='flex flex-col'>
                                    <span
                                        className={cn(
                                            'text-sm font-medium transition-colors',
                                            active
                                                ? 'text-primary-600'
                                                : 'group-hover:text-primary-600 text-gray-700'
                                        )}
                                    >
                                        {step.name}
                                    </span>
                                    {index > 0 && (
                                        <span className='inline-flex items-center gap-1 text-xs text-gray-500'>
                                            <UserIcon
                                                aria-hidden='true'
                                                className='size-3'
                                            />
                                            {step.count ?? (
                                                <span className='inline-block h-3 w-4 animate-pulse rounded bg-gray-200' />
                                            )}
                                        </span>
                                    )}
                                </span>
                            </Link>

                            {index < steps.length - 1 && (
                                <div
                                    aria-hidden='true'
                                    className='absolute top-0 right-0 hidden h-full w-5 sm:block'
                                >
                                    <svg
                                        fill='none'
                                        viewBox='0 0 22 80'
                                        preserveAspectRatio='none'
                                        className='size-full text-gray-200'
                                    >
                                        <path
                                            d='M0 -2L20 40L0 82'
                                            stroke='currentcolor'
                                            vectorEffect='non-scaling-stroke'
                                            strokeLinejoin='round'
                                        />
                                    </svg>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default Steps;
